import React, { useCallback, useEffect, useMemo } from "react";

import { renderToStaticMarkup } from 'react-dom/server';

import * as SC from './TextSegment.styled';
import useOneClickStore, {OneClickStore, Segment} from '../../Hooks/useOneClickStore';

type BlockContentProps = {
    blocks: Segment["blocks"];
};

type TextSegmentProps = {
    data: Segment;
};

export const BlockContent: React.FC<BlockContentProps> = ({blocks}) => {
    return <>
        {blocks?.map((block, index) => {
            if (block.type === 'replace' || block.type === 'insert' || block.type === 'remove') {
                return <SC.Mark key={index}>{block.copy}</SC.Mark>
            }

            if (block.type === 'common') {
                return <span key={index}>{block.copy}</span>
            }

            if (block.type === 'trace') {
                return <SC.Trace key={index}>{block.copy}</SC.Trace>
            }

            return null;
        })}
    </>;
};

const TextSegment: React.FC<TextSegmentProps> = ({data}) => {
    const {id, text, blocks, traceBlocks, isSkipped, originText, issue} = data;
    const isSegmentLoadding = useOneClickStore((store: OneClickStore) => store.isSegmentLoadding);
    const editorNode: HTMLDivElement | null = useOneClickStore((store: OneClickStore) => store.editorNode);

    const segments = useOneClickStore((store: OneClickStore) => store.segments);
    const charsRemaining = useOneClickStore((store: OneClickStore) => store.charsRemaining);
    const handleSetSingleCorrectionActiveIndex = useOneClickStore((store: OneClickStore) => store.handleSetSingleCorrectionActiveIndex);
    const isActive = useOneClickStore((store: OneClickStore) => {
        const activeIndex = store.singleCorrectionActiveIndex;

        if (activeIndex !== -1) {
            const currentIndex = store.segments.findIndex((segment) => segment.id === id);

            return currentIndex === activeIndex;
        }

        return false;
    });
    const isCorrected = !!traceBlocks?.length
    const isClickable = !isActive && !!(isCorrected || issue)

    const segmentNode = useMemo(() => {
        const spanNode = document.createElement("span");

        spanNode.classList.add('text-segment')

        if (id) {
            spanNode.id = id;
        }

        return spanNode;
    }, [id])
    const isSingleCorrectionMode = useOneClickStore((store: OneClickStore) => store.singleCorrectionActiveIndex !== -1);

    const handleSegmentSelect = useCallback((event) => {
        if (isClickable) {
            event.preventDefault();
            event.stopPropagation();

            const currentIndex = segments.findIndex((segment: Segment) => segment.id === id);

            if (currentIndex >= 0) {
                handleSetSingleCorrectionActiveIndex({value: currentIndex});
            }
        }
    }, [handleSetSingleCorrectionActiveIndex, id, isClickable, segments]);

    const isLoading = isSegmentLoadding(id);
    const isBlured = (isSingleCorrectionMode && !isActive) || (charsRemaining === 0 && !isCorrected);

    // append/cleanup dom node
    useEffect(() => {
        editorNode?.appendChild(segmentNode)

        return () => {
            segmentNode?.remove();
        }
    }, [editorNode, segmentNode]);

    useEffect(() => {
        let blocksContent = blocks?.length ? renderToStaticMarkup(<BlockContent blocks={blocks}/>) : text;

        if (isSkipped && originText) {
            blocksContent = originText;
        }

        segmentNode && (segmentNode.innerHTML = blocksContent);
    }, [blocks, isSkipped, originText, segmentNode, text]);

    useEffect(() => {
        if (isLoading) {
            segmentNode?.classList.add('loading')
        } else {
            segmentNode?.classList.remove('loading')
        }

    }, [isLoading, segmentNode]);

    useEffect(() => {
        if (isBlured) {
            segmentNode?.classList.add('blured')
        } else {
            segmentNode?.classList.remove('blured')
        }

    }, [isBlured, segmentNode]);

    useEffect(() => {
        segmentNode?.addEventListener('click', handleSegmentSelect)

        return () => {
            segmentNode?.removeEventListener('click', handleSegmentSelect)
        }
    }, [handleSegmentSelect, segmentNode]);

    return null;
}

export default TextSegment;